import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import { PrimaryLanguageEnum, ContactRelationship } from 'domains/client';
import { CODE_TO_SUBSCRIBER_RELATIONSHIP_MAP } from 'domains/signupDirect/businessLogic/relationships';
import { DirectSignupFilters, ResumedLeadState } from 'domains/signupDirect/context';
import { reverseConcernsFromAreas, SignupClinicalArea } from 'domains/signupDirect/misc/communicationConcerns';
import { SIGNUP_PRIVATE_PAY_OPTION, TherapyTargetValue } from 'domains/signupDirect/misc/constants';
import { getInsuranceV2Options, InsuranceDefinitionV2 } from 'domains/signupDirect/misc/insuranceOptions';
import { getIncompleteLeadData, SignupResumeLeadPayload } from 'domains/signupDirect/ResumeLead/data/queries';
import { EvaluationFormType, getInsuranceSchemaType } from 'domains/signupDirect/schemas/evaluationSchema';
import { get } from 'lodash';
import { DeepPartial } from 'react-hook-form';
import { parseDOBToDate } from 'utils/date';
import { StateAbbrev } from 'utils/states';
import { SignupError } from 'utils/errors/debuggableError';

export type ResumedSignupData =
  | {
      expired: true;
    }
  | {
      expired: false;
      filters: DeepPartial<DirectSignupFilters>;
      incompleteEvalForm: DeepPartial<EvaluationFormType>;
      resumedLeadState: ResumedLeadState;
    };
export const resumeSignupFromLeadID = async (
  leadID: string,
  data: SignupResumeLeadPayload,
): Promise<ResumedSignupData | undefined> => {
  const rawResults: ResumedSignupData = {
    expired: false,
    filters: {
      clinicalConcernKeys: [],
    },
    incompleteEvalForm: {
      clientInfo: {},
      billingInfo: {},
    },
    resumedLeadState: {
      leadID,
      shouldSkipEligibility: false,
    },
  };
  try {
    const leadData = await getIncompleteLeadData(leadID, data);

    if (leadData.expired) {
      return {
        expired: true,
      };
    }

    // Payment method
    const {
      paymentMethod: { entryId, type },
    } = leadData.filters;

    const isInsurance = type === 'insurance';
    const isPrivatePay = type === 'privatePay';

    let matchedInsurance: InsuranceDefinitionV2 | undefined = undefined;
    if (isInsurance) {
      const insuranceData = await getInsuranceV2Options();

      matchedInsurance = insuranceData.find(insurance => insurance.entryId === entryId);
      if (matchedInsurance) {
        rawResults.filters.paymentMethod = {
          type: 'insurance',
          id: matchedInsurance.entryId,
          insuranceData: matchedInsurance,
        };

        if (leadData.filters.paymentMethod.shouldSkipEligibility) {
          rawResults.resumedLeadState.shouldSkipEligibility = true;
        }
      }
    } else if (isPrivatePay) {
      rawResults.filters.paymentMethod = {
        type: 'privatePay',
        id: SIGNUP_PRIVATE_PAY_OPTION,
      };
    } else if (type === 'otherInsurance') {
      rawResults.filters.paymentMethod = undefined;
    }

    // Language

    rawResults.filters.language = leadData.filters.primaryLanguage as PrimaryLanguageEnum;

    // Clinical concerns

    const { detectedAreas } = leadData.filters;

    if (detectedAreas) {
      rawResults.filters.clinicalConcernKeys = reverseConcernsFromAreas(
        detectedAreas as SignupClinicalArea[],
        leadData.filters.servicesFor as TherapyTargetValue,
      );
    }

    // State

    rawResults.filters.stateCode = leadData.filters.address.state as StateAbbrev;

    // Target

    rawResults.filters.therapyTarget = leadData.filters.servicesFor as TherapyTargetValue;

    // Evaluation data

    rawResults.incompleteEvalForm = {
      ...rawResults.incompleteEvalForm,
      clientType: leadData.filters.servicesFor as TherapyTargetValue,
      clientInfo: {
        firstName: leadData.client.firstName,
        lastName: leadData.client.lastName,
        dateOfBirth: leadData.client.dateOfBirth
          ? // @ts-expect-error Upstream issue with type from the validation vs type of the input
            (format(parseDOBToDate(leadData.client.dateOfBirth, 'yyyy-MM-dd'), 'MM/dd/yyyy') as Date)
          : undefined,
        language: leadData.filters.primaryLanguage as PrimaryLanguageEnum,
        stateCode: leadData.filters.address.state as StateAbbrev,
      },
      loginContactInfo: {
        email: leadData.contact.email,
      },
      contactInfo: {
        phone: leadData.contact.phone,
        firstName: leadData.contact.firstName,
        lastName: leadData.contact.lastName,
        relationshipToClient: leadData.contact.relationshipToClient as ContactRelationship,
      },
      primaryPhysician: {
        name: leadData.primaryPhysician.name,
        officeName: leadData.primaryPhysician.clinicOrg,
        phone: leadData.primaryPhysician.phone,
        website: leadData.primaryPhysician.website,
      },
    };

    if (matchedInsurance) {
      // @ts-expect-error Since we are mixing data from all possible combinations of insurance types
      rawResults.incompleteEvalForm.billingInfo = {
        type: getInsuranceSchemaType(matchedInsurance.insuranceType),
        insuranceData: matchedInsurance,

        subscriber: {
          dateOfBirth: leadData.insurance.subscriberDateOfBirth
            ? format(parseDOBToDate(leadData.insurance.subscriberDateOfBirth, 'yyyy-MM-dd'), 'MM/dd/yyyy')
            : undefined,
          firstName: leadData.insurance.subscriberFirstName,
          lastName: leadData.insurance.subscriberLastName,
          relationShipToClient: leadData.insurance.relationshipToClient
            ? CODE_TO_SUBSCRIBER_RELATIONSHIP_MAP[leadData.insurance.relationshipToClient]
            : undefined,
        },
        memberId: leadData.insurance.memberId,
        groupId: leadData.insurance.groupId || undefined,
      };
    }

    if (leadData.filters.paymentMethod.shouldSkipEligibility) {
      rawResults.incompleteEvalForm.billingInfo = {
        ...rawResults.incompleteEvalForm.billingInfo,
        preApprovedEligibilityCheck: true,
      };
    }
  } catch (e) {
    if (get(e, 'originalError.response.status') === 401) {
      return undefined;
    }

    // eslint-disable-next-line no-console
    console.warn('Failed to load saved data from endpoint', e);

    captureException(
      new SignupError(`Signup Failed to load saved data from endpoint with LeadID ${leadID}`, {
        debugData: {
          e,
          message: get(e, 'message'),
        },
      }),
    );

    return undefined;
  }

  return rawResults;
};
